/* istanbul ignore file */
import React, { FC, useState, useCallback, useContext, useMemo, useEffect } from "react";
import {
  Card,
  Icon,
  CardWrapper,
  CheckBox,
  Label,
  useRequestInit,
  Notificaties2Context,
  useFeature,
  LoadingSpinner
} from "adviesbox-shared";
import { RouteParams } from "../../../shared/paramrouting/paramrouting-context";
import { ProductOverzicht } from "../productoverzicht/product-overzicht";
import { Formik, FormikProps } from "formik";
import LastenOverzichtChart from "../lastenoverzicht-chart/lastenoverzicht-chart";
import LastenOverzichtCard from "../lastenoverzicht-card/lastenoverzicht-card";
import { Lasten, LastenOverzichtV2 } from "../../../.generated/rapportage/rapportagetypes";
import "./snel-inzicht-drawer.scss";
import classNames from "classnames";
import { IconButton } from "@material-ui/core";
import { useCookieBoolean } from "../../../useCookieSettings";
import { AppDataContext } from "../../../navigation/appdata-context";
import { Table, Spinner, Button } from "react-bootstrap";
import { HypotheekOverzichtFetcher } from "./hypotheek-overzicht-fetcher";
import { LastenOverzichtFetcher } from "./lasten-overzicht-fetcher";
import { VerschilRij } from "./verschil-rij";
import { NieuwRapport } from "../../../.generated/foundation/foundationtypes";
import { useParams } from "react-router-dom";
import { Situatiekeuze, DashboardOutput, DashboardSnelInzicht } from "../../../.generated/forms/formstypes";

import { saveDataRequestInit as createSaveData } from "../../../shared/utils/save-data";
import { mapDashboardUiToDl } from "../../infra/map-dashboard-ui-2-dl";
import { useFetchData } from "../../../shared/hooks/usefetchdata";

// Helper function to extract snel inzicht data from dashboard output
const getSnelInzicht = (data: DashboardOutput): DashboardSnelInzicht => {
  const dash = data.dashboards?.[Object.keys(data.dashboards)[0]];
  return (
    dash?.snelInzicht ?? {
      situatieKeuze: Situatiekeuze.Geen,
      situatieKeuzes: [Situatiekeuze.Geen]
    }
  );
};

// Mapping functions for situatiekeuze - simplified to work with arrays of string values
const situatiekeuzeToSelectedVoorstellen = (
  situatiekeuzes: Situatiekeuze[] | string,
  voorstellen: { voorstelId: string; naam: string }[]
): { voorstelId: string; checked: boolean }[] | null => {
  const result: { voorstelId: string; checked: boolean }[] = [];

  // Normalize situatiekeuzes to array
  let situatiekeuzesArray: Situatiekeuze[];
  if (typeof situatiekeuzes === "string") {
    // Handle comma-delimited string
    situatiekeuzesArray = situatiekeuzes
      .split(",")
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .map(s => s as Situatiekeuze);
  } else {
    situatiekeuzesArray = situatiekeuzes;
  }

  // Map voorstellen to situatie positions
  voorstellen.forEach((voorstel, index) => {
    let checked = false;

    if (index === 0 && situatiekeuzesArray.includes(Situatiekeuze.Situatie1)) checked = true;
    if (index === 1 && situatiekeuzesArray.includes(Situatiekeuze.Situatie2)) checked = true;
    if (index === 2 && situatiekeuzesArray.includes(Situatiekeuze.Situatie3)) checked = true;

    result.push({ voorstelId: voorstel.voorstelId, checked });
  });
  // Add "Huidige situatie" (voorstelId: "999")
  result.push({
    voorstelId: "999",
    checked: situatiekeuzesArray.includes(Situatiekeuze.HuidigeSituatie)
  });

  return result;
};

const selectedVoorstellenToSituatiekeuze = (
  selectedVoorstellen: { voorstelId: string; checked: boolean }[],
  voorstellen: { voorstelId: string; naam: string }[]
): Situatiekeuze[] => {
  const result: Situatiekeuze[] = [];

  // Check for "Huidige situatie" first
  const huidigeSituatie = selectedVoorstellen.find(v => v.voorstelId === "999" && v.checked);
  if (huidigeSituatie) {
    result.push(Situatiekeuze.HuidigeSituatie);
  }

  // Find checked voorstellen and add their corresponding situaties
  const checkedVoorstellen = selectedVoorstellen.filter(v => v.checked && v.voorstelId !== "999");

  checkedVoorstellen.forEach(checked => {
    const voorstelIndex = voorstellen.findIndex(v => v.voorstelId === checked.voorstelId);

    if (voorstelIndex === 0) result.push(Situatiekeuze.Situatie1);
    else if (voorstelIndex === 1) result.push(Situatiekeuze.Situatie2);
    else if (voorstelIndex === 2) result.push(Situatiekeuze.Situatie3);
  });

  // Return empty array if nothing is selected, otherwise return the selected situaties
  return result.length === 0 ? [Situatiekeuze.Geen] : result;
};

export type KeuzeOpties = {
  MaandlastenoverzichtRadioButtons: string;
  selectedVoorstellen: { voorstelId: string; checked: boolean }[];
  "Overzicht vergelijking hypotheeklastenRadioButtons": string;
  situatieKeuze?: Situatiekeuze;
};

type SnelInzichtDrawerProps = {
  values?: KeuzeOpties;
  situatie?: string;
};

// Component for rendering checkboxes with direct data fetching
const VoorstellenSelector: FC<{
  situatie: string;
  onSelectionChange: (selectedVoorstellen: { voorstelId: string; checked: boolean }[]) => void;
  setFieldValue: (field: string, value: any) => void;
}> = ({ situatie, onSelectionChange, setFieldValue }) => {
  const { voorstellen } = useContext(AppDataContext);
  const [selectedVoorstellen, setSelectedVoorstellen] = useState<{ voorstelId: string; checked: boolean }[]>([]);
  const { params, settings, requestInit } = useRequestInit<{
    vestiging: string;
    adviesdossier: string;
    voorstel: string;
  }>();

  const voorstellenWithHuidige = [...voorstellen, { voorstelId: "999", naam: "Huidige situatie" }];

  // Create saveData function for persisting situatiekeuze
  const getDashboardUrl = useMemo(() => {
    const baseUrl = `${settings?.klantdossiersFormsOrigin}`;
    return `${baseUrl}/Adviesdossiers/${params.adviesdossier}/Dashboard/SnelInzicht`;
  }, [params.adviesdossier, settings]);

  const saveData = useCallback(
    createSaveData(
      getDashboardUrl,
      requestInit,
      mapDashboardUiToDl,
      () => null,
      () => null
    ),
    [getDashboardUrl, requestInit]
  );

  const getSnelInzichtUrl = useMemo(() => {
    const baseUrl = `${settings?.klantdossiersFormsOrigin}`;
    const url = `${baseUrl}/Adviesdossiers/${params.adviesdossier}/Dashboard/SnelInzicht?berekenTekort=false`;
    return url;
  }, [params.adviesdossier, settings]);

  const getRequestInit = useCallback(
    () => ({
      ...requestInit,
      method: "GET"
    }),
    [requestInit]
  );

  const { data, loading, error } = useFetchData<DashboardOutput>(
    getSnelInzichtUrl,
    getRequestInit,
    !!getSnelInzichtUrl
  );

  // Process the fetched data and set initial values
  useEffect(() => {
    let isMounted = true;

    if (data && voorstellen.length > 0 && isMounted) {
      const snelInzichtData = getSnelInzicht(data);
      const situatiekeuze = snelInzichtData.situatieKeuze;

      // Convert situatiekeuzes to selectedVoorstellen
      const initialSelectedVoorstellen = situatiekeuzeToSelectedVoorstellen(
        snelInzichtData.situatieKeuzes || situatiekeuze,
        voorstellen
      );

      const finalSelectedVoorstellen =
        initialSelectedVoorstellen && initialSelectedVoorstellen.some(item => item.checked)
          ? initialSelectedVoorstellen
          : voorstellen.map(v => ({
              voorstelId: (v as any).voorstelId,
              checked: (v as any).voorstelId === params.voorstel
            }));

      if (isMounted) {
        setSelectedVoorstellen(finalSelectedVoorstellen);
        setFieldValue("selectedVoorstellen", finalSelectedVoorstellen);

        // Notify parent component about the initial selection
        onSelectionChange(finalSelectedVoorstellen);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [data, voorstellen, setFieldValue, onSelectionChange, params.voorstel]);

  if (loading) {
    return <LoadingSpinner size="S" />;
  }

  if (error) {
    return <div>Er is een fout opgetreden bij het ophalen van de gegevens. Details: {error}</div>;
  }

  if (!data) {
    return <div>Geen gegevens beschikbaar.</div>;
  }

  const snelInzichtData = getSnelInzicht(data);
  const situatiekeuze = snelInzichtData.situatieKeuze;

  // Convert situatiekeuzes to selectedVoorstellen if not already set
  const currentSelectedVoorstellen =
    selectedVoorstellen.length > 0
      ? selectedVoorstellen
      : voorstellen.length > 0
      ? situatiekeuzeToSelectedVoorstellen(snelInzichtData.situatieKeuzes || situatiekeuze, voorstellen)
      : voorstellen.map(v => ({ voorstelId: v.voorstelId, checked: v.voorstelId === params.voorstel }));

  const handleVoorstelChange = async (index: number): Promise<void> => {
    const newSelectedVoorstellen = voorstellenWithHuidige.map((v, i) => ({
      voorstelId: v.voorstelId,
      checked:
        i === index
          ? !currentSelectedVoorstellen.find(sv => sv.voorstelId === v.voorstelId)?.checked
          : currentSelectedVoorstellen.find(sv => sv.voorstelId === v.voorstelId)?.checked ?? false
    }));
    setSelectedVoorstellen(newSelectedVoorstellen);
    // Only update Formik if there are actual changes
    if (
      newSelectedVoorstellen.length !== selectedVoorstellen.length ||
      newSelectedVoorstellen.some(
        (newItem, index) =>
          newItem.voorstelId !== selectedVoorstellen[index]?.voorstelId ||
          newItem.checked !== selectedVoorstellen[index]?.checked
      )
    ) {
      setFieldValue("selectedVoorstellen", newSelectedVoorstellen);
    }
    onSelectionChange(newSelectedVoorstellen);

    // Convert to situatiekeuze and persist to backend
    const newSituatiekeuze = selectedVoorstellenToSituatiekeuze(newSelectedVoorstellen, voorstellen);

    // Get the current dashboard data and update the snelInzicht part
    const dashboardData = data.dashboards?.[Object.keys(data.dashboards)[0]];
    if (dashboardData) {
      const updatedDashboard = {
        ...dashboardData,
        snelInzicht: {
          situatieKeuzes: newSituatiekeuze
        }
      };

      await saveData(updatedDashboard);
    }
  };

  const checkedVoorstellen = currentSelectedVoorstellen.filter(v => v.checked) || [];

  return (
    <div className="d-flex flex-row px-2">
      <Label caption={"Vergelijk de hypotheeklasten van (maximaal 3 voorstellen):"} />
      {voorstellenWithHuidige.map((voorstel, index) => (
        <CheckBox
          key={voorstel.voorstelId}
          caption={`${voorstel.naam}`}
          name={`selectedVoorstellen[${index}].checked`}
          onChange={() => handleVoorstelChange(index)}
          disabled={
            !currentSelectedVoorstellen.find(sv => sv.voorstelId === voorstel.voorstelId)?.checked &&
            checkedVoorstellen.length >= 3
          }
        />
      ))}
    </div>
  );
};

export const SnelInzichtDrawer: FC<SnelInzichtDrawerProps> = ({ values, situatie = "huidig" }) => {
  const [lasten, setLasten] = useState<Lasten & { gebeurtenissen: string[] }>();
  const [isCollapsed, setCollapsed] = useCookieBoolean("scenario-instellingen-productoverzicht-weergeven", false);
  const { voorstellen } = useContext(AppDataContext);
  const [eersteLasten, setEersteLasten] = useState<LastenOverzichtV2 | null>(null);
  const [tweedeLasten, setTweedeLasten] = useState<LastenOverzichtV2 | null>(null);
  const [currentSelectedVoorstellen, setCurrentSelectedVoorstellen] = useState<
    { voorstelId: string; checked: boolean }[]
  >([]);
  const { params } = useRequestInit();

  // Fetch snel inzicht data for initial values
  const { settings: snelInzichtSettings, params: snelInzichtParams } = useRequestInit<{
    vestiging: string;
    adviesdossier: string;
    voorstel: string;
  }>();

  const getSnelInzichtUrl = useMemo(() => {
    const baseUrl = `${snelInzichtSettings?.klantdossiersFormsOrigin}`;
    const url =
      situatie === "voorstel"
        ? `${baseUrl}/Voorstellen/${snelInzichtParams.voorstel}/Dashboard/SnelInzicht?berekenTekort=false`
        : `${baseUrl}/Adviesdossiers/${snelInzichtParams.adviesdossier}/Dashboard/SnelInzicht?berekenTekort=false`;
    return url;
  }, [snelInzichtParams.adviesdossier, snelInzichtParams.voorstel, situatie, snelInzichtSettings]);

  const getSnelInzichtRequestInit = useCallback(
    () => ({
      method: "GET"
    }),
    []
  );

  const { data: snelInzichtData, loading: snelInzichtLoading } = useFetchData<DashboardOutput>(
    getSnelInzichtUrl,
    getSnelInzichtRequestInit,
    !!getSnelInzichtUrl
  );

  // Calculate initial values based on fetched data
  const initialValues = useMemo(() => {
    // If values are provided as props, use them first
    if (values) {
      return {
        MaandlastenoverzichtRadioButtons: values.MaandlastenoverzichtRadioButtons || "tabel",
        selectedVoorstellen: values.selectedVoorstellen || [],
        "Overzicht vergelijking hypotheeklastenRadioButtons":
          values["Overzicht vergelijking hypotheeklastenRadioButtons"] || "tabel"
      };
    }

    if (snelInzichtLoading || !snelInzichtData || !voorstellen.length) {
      // Fallback to basic initial values
      return {
        MaandlastenoverzichtRadioButtons: "tabel",
        selectedVoorstellen: params.voorstel
          ? voorstellen.map(v => ({ voorstelId: v.voorstelId, checked: v.voorstelId === params.voorstel }))
          : [],
        "Overzicht vergelijking hypotheeklastenRadioButtons": "tabel"
      };
    }

    const snelInzicht = getSnelInzicht(snelInzichtData);
    const initialSelectedVoorstellen = situatiekeuzeToSelectedVoorstellen(
      snelInzicht.situatieKeuzes || snelInzicht.situatieKeuze,
      voorstellen
    );

    return {
      MaandlastenoverzichtRadioButtons: "tabel",
      selectedVoorstellen: initialSelectedVoorstellen,
      "Overzicht vergelijking hypotheeklastenRadioButtons": "tabel"
    };
  }, [values, snelInzichtLoading, snelInzichtData, voorstellen, params.voorstel]);

  // Set current selected voorstellen when data is loaded
  useEffect(() => {
    let isMounted = true;

    if (snelInzichtData && voorstellen.length > 0 && isMounted) {
      const snelInzicht = getSnelInzicht(snelInzichtData);
      const initialSelectedVoorstellen = situatiekeuzeToSelectedVoorstellen(
        snelInzicht.situatieKeuzes || snelInzicht.situatieKeuze,
        voorstellen
      );
      if (isMounted) {
        setCurrentSelectedVoorstellen(initialSelectedVoorstellen);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [snelInzichtData, voorstellen]);

  const handleDataLoaded = useCallback((data: LastenOverzichtV2, isFirst: boolean) => {
    if (isFirst) {
      setEersteLasten(data);
    } else {
      setTweedeLasten(data);
    }
  }, []);

  const paramContext = useParams<RouteParams>();
  const { settings, user } = useRequestInit<{ vestiging: string; adviesdossier: string }>();
  const [isPrinting, setIsPrinting] = useState(false);
  const { subscribeCorrelationId } = useContext(Notificaties2Context);
  const featureRapport2Overzicht = useFeature("FeatureRapport2Overzicht");

  const handleSelectionChange = useCallback((selectedVoorstellen: { voorstelId: string; checked: boolean }[]) => {
    setCurrentSelectedVoorstellen(selectedVoorstellen);
  }, []);

  const voorstellenWithHuidige = [...voorstellen, { voorstelId: "999", naam: "Huidige situatie" }];

  const getRequestInit = useCallback(
    (correlationId: string): RequestInit => {
      // Determine rapportStructuurId based on number of selected voorstellen
      const checkedVoorstellen = currentSelectedVoorstellen.filter(v => v.checked) || [];
      const rapportStructuurId =
        checkedVoorstellen.length > 1 ? "00000000-0000-0000-0000-000000000009" : "00000000-0000-0000-0000-000000000001";

      const nieuwRapport: NieuwRapport = {
        adviesDossierId: paramContext.adviesdossier,
        voorstelId: paramContext.voorstel || null,
        rapportStructuurId: rapportStructuurId,
        gewensteRapportNaam: null,
        preview: false
      };

      const formData = new FormData();
      formData.append("JsonValues", JSON.stringify(nieuwRapport));

      return {
        headers: {
          authorization: `${user?.token_type} ${user?.access_token}`,
          "Ocp-Apim-Subscription-Key": settings.OcpApimSubscriptionKey,
          vestigingId: paramContext.vestiging,
          correlationId: correlationId
        },
        method: "POST",
        body: formData
      };
    },
    [settings, user, paramContext, currentSelectedVoorstellen]
  );

  /* istanbul ignore next */
  const requestInitNieuwRapport = useCallback(
    (correlationId: string): RequestInit => {
      return getRequestInit(correlationId);
    },
    [getRequestInit]
  );

  const verwerkEnSubscribe = useCallback(async (): Promise<void> => {
    setIsPrinting(true);
    const result = await subscribeCorrelationId(
      `${settings.foundationOrigin}/FrontBus/nieuw-rapport`,
      requestInitNieuwRapport,
      /* istanbul ignore next */ () => {
        setIsPrinting(false);
      }
    );
    if (!result) {
      // setErrorMessage("Er is een fout opgetreden bij het verwerken van de gegevens.");
      setIsPrinting(false);
    }
  }, [setIsPrinting, settings, requestInitNieuwRapport, subscribeCorrelationId]);

  const checkedVoorstellen = currentSelectedVoorstellen.filter(v => v.checked) || [];
  const isCollapsedWrap = isCollapsed || checkedVoorstellen.length > 1;

  // Show loading state while fetching initial data
  if (snelInzichtLoading) {
    return (
      <div className="snel-inzicht">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
          <LoadingSpinner size="L" />
        </div>
      </div>
    );
  }

  return (
    <Formik initialValues={initialValues} onSubmit={/* istanbul ignore next */ () => {}}>
      {({
        values,
        setFieldValue
      }: FormikProps<{
        MaandlastenoverzichtRadioButtons: string;
        selectedVoorstellen: { voorstelId: string; checked: boolean }[];
        "Overzicht vergelijking hypotheeklastenRadioButtons": string;
      }>) => {
        let eersteTotaalNetto = 0;
        if (eersteLasten?.totalen?.nettoLast != null) eersteTotaalNetto = eersteLasten.totalen.nettoLast * 12;

        let tweedeTotaalNetto = 0;
        if (tweedeLasten?.totalen?.nettoLast != null) tweedeTotaalNetto = tweedeLasten.totalen.nettoLast * 12;

        return (
          <div className="snel-inzicht">
            <CardWrapper maxRowCount={2} flexType="flex-row">
              <div
                className={classNames("snel-inzicht__column col-12", {
                  "col-md-1": isCollapsedWrap,
                  "col-md-5 col-xl-3": !isCollapsedWrap
                })}
              >
                <Card
                  title={!isCollapsedWrap ? "Productoverzicht" : ""}
                  titlePrepend={
                    <IconButton
                      title="Instellingen"
                      className="snel-inzicht__overzicht-button"
                      onClick={() => setCollapsed(!isCollapsedWrap)}
                    >
                      <Icon name="hamburger" iconSize="l" />
                    </IconButton>
                  }
                >
                  <ProductOverzicht isCollapsed={isCollapsedWrap} />
                </Card>
              </div>
              <div
                className={classNames("snel-inzicht__column snel-inzicht__no-padding col-12", {
                  "col-md-11": isCollapsedWrap,
                  "col-md-7 col-xl-9": !isCollapsedWrap
                })}
              >
                <div>
                  <div className="d-flex flex-wrap flex-row">
                    <Card
                      title="Overzicht vergelijking hypotheeklasten"
                      radioOptions={[
                        { label: "Tabel", value: "tabel" },
                        { label: "Grafiek", value: "grafiek" }
                      ]}
                      className="col-12"
                    >
                      {values["Overzicht vergelijking hypotheeklastenRadioButtons"] === "tabel" && (
                        <>
                          {featureRapport2Overzicht && (
                            <div className="d-flex justify-content-end">
                              <Button
                                style={{ position: "absolute", right: "156px", marginTop: "-50px" }}
                                onClick={async (): Promise<void> => {
                                  await verwerkEnSubscribe();
                                }}
                                disabled={isPrinting}
                              >
                                {isPrinting ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <Icon name="download" iconSize="l" />
                                )}
                              </Button>
                            </div>
                          )}
                          <VoorstellenSelector
                            situatie={situatie}
                            onSelectionChange={handleSelectionChange}
                            setFieldValue={setFieldValue}
                          />

                          {!!(checkedVoorstellen.length > 1) && (
                            <div className="d-flex flex-wrap flex-row">
                              <div
                                className={classNames("d-flex flex-wrap", {
                                  "col-10": checkedVoorstellen.length === 2,
                                  "col-12": checkedVoorstellen.length !== 2
                                })}
                              >
                                {checkedVoorstellen.map((voorstel, index) => {
                                  const voorstelId = voorstellenWithHuidige.find(
                                    v => v.voorstelId === voorstel.voorstelId
                                  )?.voorstelId;
                                  return voorstelId ? (
                                    <div
                                      key={voorstelId}
                                      className={`col-${12 /
                                        checkedVoorstellen.length} p-0  d-flex flex-column justify-content-between`}
                                    >
                                      <HypotheekOverzichtFetcher index={index} voorstelId={voorstelId} />
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}
                          <div className="d-flex flex-wrap flex-row">
                            <div
                              className={classNames("d-flex flex-wrap", {
                                "col-10": checkedVoorstellen.length === 2,
                                "col-12": checkedVoorstellen.length !== 2
                              })}
                            >
                              {checkedVoorstellen.map((voorstel, index) => {
                                const voorstelId = voorstellenWithHuidige.find(
                                  v => v.voorstelId === voorstel.voorstelId
                                )?.voorstelId;
                                return voorstelId ? (
                                  <div key={voorstelId} className={`col-${12 / checkedVoorstellen.length} p-0`}>
                                    <LastenOverzichtFetcher
                                      key={`${voorstelId}-${index}`}
                                      voorstelId={voorstelId}
                                      beperkteLastenTonen={checkedVoorstellen.length > 1}
                                      onDataLoaded={data => {
                                        handleDataLoaded(data, index === 0);
                                      }}
                                      jarenTonen={index === 0}
                                    />
                                  </div>
                                ) : null;
                              })}
                            </div>
                            {checkedVoorstellen.length === 2 && (
                              <div className="col-2 p-0">
                                <Table striped bordered hover responsive className="scenario-table">
                                  <thead>
                                    <tr>
                                      <th>Verschil in netto last</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {eersteLasten?.jaarGegevens?.map((jaarData, index) => (
                                      <tr key={jaarData.jaar}>
                                        <VerschilRij
                                          eerste={jaarData.nettoLast ?? null}
                                          tweede={tweedeLasten?.jaarGegevens?.[index]?.nettoLast ?? null}
                                        />
                                      </tr>
                                    ))}
                                    {!eersteLasten &&
                                      Array.from({ length: 30 }).map((_, index) => (
                                        <tr key={index}>
                                          <td>&nbsp;</td>
                                        </tr>
                                      ))}
                                    {eersteLasten?.totalen && tweedeLasten?.totalen && (
                                      <tr>
                                        <VerschilRij
                                          eerste={eersteTotaalNetto ?? null}
                                          tweede={tweedeTotaalNetto ?? null}
                                        />
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                      {values["Overzicht vergelijking hypotheeklastenRadioButtons"] === "grafiek" && (
                        <LastenOverzichtChart
                          onHoverCallback={lasten => {
                            setLasten(lasten);
                          }}
                          onClickCallback={lasten => {
                            setLasten(lasten);
                          }}
                          data={eersteLasten}
                        />
                      )}
                    </Card>
                    {values["Overzicht vergelijking hypotheeklastenRadioButtons"] === "grafiek" && lasten && (
                      <Card title={`Jaar ${lasten.jaar}`} className="col-12">
                        <LastenOverzichtCard lasten={lasten} />
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            </CardWrapper>
          </div>
        );
      }}
    </Formik>
  );
};
